import { createSelector } from 'reselect';

import { IState } from 'src/app/core/redux/interfaces';
import {
  ICombinedIdpData,
  IIdp,
  IIdpData,
} from 'src/app/interfaces/IIdentityProvider';
import { FEATURE_FLAG_CONSTANTS } from 'src/app/utils/constant';
import remoteSdk from 'src/platform-sdk/initialized-platform-sdk';
import { hasFeatureFlags } from '../common/selectors';

export const getState = (state: IState): IIdp => state.idp || {};

export const getIdp = createSelector([getState], ({ idp }) => idp?.data ?? []);

export const getExternalIdp = createSelector(
  [getState],
  ({ externalIdp }) => externalIdp?.data ?? []
);

export const getAllCombinedIdp = createSelector(
  [getState],
  ({ combinedIdp }) => {
    const data = combinedIdp?.data ?? [];
    return data;
  }
);

export const getExternalIdpLoadingStatus = createSelector(
  [getState],
  ({ externalIdp }) => externalIdp?.loading ?? false
);

export const getIdpData = createSelector([getIdp], (idps): IIdpData[] =>
  idps.map((item) => ({
    id: item.id,
    name: item.inputs.idpName,
    link: item.outputs.acsUrl,
    status: item.outputs.isEnabled,
    isExternal: false,
    groupMappings: undefined,
  }))
);

export const getCombinedIdpData = createSelector(
  [getAllCombinedIdp],
  (combinedIdps): ICombinedIdpData[] => {
    const result = combinedIdps.map((item) => ({
      id: item.idpId,
      name: item.idpName,
      link: item.idpUrl,
      status: true,
      isExternal: false,
      groupMappings: undefined,
      type: item.idpType,
    }));
    return result;
  }
);

export const getIsAddExternalIdpModalVisible = createSelector(
  [getState],
  ({ addExternalIdp }) =>
    addExternalIdp?.addExternalIdpModal?.isModalVisible ?? false
);

export const getAddExternalIdpModalStep = createSelector(
  [getState],
  ({ addExternalIdp }) => addExternalIdp?.addExternalIdpModal?.step ?? 'add'
);

export const getAddExternalIdpModalIdpIdInputValue = createSelector(
  [getState],
  ({ addExternalIdp }) =>
    addExternalIdp?.addExternalIdpModal?.idpIdInputValue ?? ''
);

export const getIsExternalIdpModalIdpIdInputValueValid = createSelector(
  [getState],
  ({ addExternalIdp }) =>
    addExternalIdp?.addExternalIdpModal.isIdpIdInputValueValid
);

export const getVerifyExternalIdpLoadingStatus = createSelector(
  [getState],
  ({ addExternalIdp }) =>
    addExternalIdp?.externalIdpToBeAdded?.isLoading ?? false
);

export const getAddExternalIdpData = createSelector(
  [getState],
  ({ addExternalIdp }) => addExternalIdp?.externalIdpToBeAdded?.data ?? null
);

export const getAddExternalCUIIdp = createSelector(
  [getState],
  ({ addExternalIdp }) => {
    const data = addExternalIdp?.externalIdpToBeAdded?.data ?? null;
    return data;
  }
);

export const getAddExternalIdpError = createSelector(
  [getState],
  ({ addExternalIdp }) => addExternalIdp?.externalIdpToBeAdded?.error ?? null
);

export const getIdpNotification = createSelector(
  [getState],
  ({ notification }) => ({
    notificationType: notification?.type,
    traceId: notification?.traceId ?? null,
  })
);

export const getIsDeleteExternalIdpModalVisible = createSelector(
  [getState],
  ({ deleteExternalIdp }) =>
    deleteExternalIdp?.deleteExternalIdpModal?.isModalVisible ?? false
);

export const getDeleteExternalIdpData = createSelector(
  [getState],
  ({ deleteExternalIdp }) =>
    deleteExternalIdp?.externalIdpToBeDeleted?.data ?? null
);

export const getDeleteExternalIdpLoadingStatus = createSelector(
  [getState],
  ({ deleteExternalIdp }) => deleteExternalIdp?.isLoading ?? false
);

export const getExternalIdpData = createSelector(
  [getExternalIdp],
  (idps): IIdpData[] =>
    idps.map((item) => ({
      id: item.idpId,
      name: item.idpName,
      link: item?.samlIssuerUrl ?? '',
      status: true,
      isExternal: true,
      groupMappings: item.groupMappings,
    }))
);

export const getAllIdps = createSelector(
  [
    getIdpData,
    getExternalIdpData,
    (state) => hasFeatureFlags(state, [FEATURE_FLAG_CONSTANTS.EXTERNAL_IDP]),
    getCombinedIdpData,
  ],
  (
    idp: IIdpData[],
    externalIdp: IIdpData[],
    hasExternalIdpFeatureFlag: boolean,
    combinedIdpData: ICombinedIdpData[]
  ): IIdpData[] | ICombinedIdpData[] => {
    let idpsResult: IIdpData[] | ICombinedIdpData[];

    if (
      remoteSdk?.featureFlags?.isEnabled(FEATURE_FLAG_CONSTANTS.ENABLE_CUI_MFE)
    ) {
      externalIdp.forEach((idp) => {
        try {
          idp.name = atob(idp.id).split(':')[0];
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error decoding idp.id:', error);
          idp.name = idp.id;
        }
        if (!idp.link) {
          idp.link = idp.name;
        }
      });
      idpsResult = [
        ...combinedIdpData,
        ...(hasExternalIdpFeatureFlag ? externalIdp : []),
      ];
    } else {
      idpsResult = [...idp, ...(hasExternalIdpFeatureFlag ? externalIdp : [])];
    }
    return idpsResult;
  }
);
